import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from "@angular/core";
import {SessionModel} from "../../orchestration/session.model";
import {PromoEvent} from "../promo-event";
import {IdentityModel} from "../../identity/identity.model";
import {TouchableDirective} from "../../ui-elements/touchable.directive";
import {Subscription} from "rxjs";
import {Direction} from "../../common-types/direction";
import {IconButtonType} from "../../buttons/icon-button/icon-button";
import {NavigationService} from "../../orchestration/navigation.service";
import {IdentificationMode} from "../../identity/identification-page/identification-mode";
import {AnalyticsService} from "../../orchestration/analytics.service";
import {SessionEvents} from "../../orchestration/session-events";
import {AppPromoData} from "../../identity/member-summary-entity.model";
import {AppDealActionStyle} from "../../orchestration/app-entity.model";
import {EntitlementData} from "../../deal/entitlement-entity.model";
import {MenuItemData} from "../../menu/menu-entity.model";

enum SlideType {
    Login,
    Deal,
    Promo,
    More
}

enum ScrollType {
    Auto,
    Swipe,
    Click
}

class PromoSlide {
    constructor (public type: SlideType,
                 public data?: EntitlementData | AppPromoData) {
    }

    get entitlement(): EntitlementData {
        return this.type === SlideType.Deal ? <EntitlementData> this.data : undefined;
    }

    get promo(): AppPromoData {
        return this.type === SlideType.Promo ? <AppPromoData> this.data : undefined;
    }

    get slideId(): string {
        return this.type === SlideType.Deal ? this.entitlement.id
            : this.type === SlideType.Promo ? this.promo.id
                : this.type === SlideType.Login ? "LOGIN"
                    : this.type === SlideType.More ? "MORE"
                        : undefined;
    }
}

@Component({
    selector: "woa-promo-carousel",
    templateUrl: "./promo-carousel.component.html"
})
export class PromoCarouselComponent implements OnInit, OnChanges, OnDestroy {
    private static readonly AUTO_SCROLL_MILLIS: number = 5000;

    @Input() promoteMemberLogin: boolean = true;
    @Input() deals: EntitlementData[] = [];
    @Input() promos: AppPromoData[] = [];
    @Input() morePromotionalCards: boolean;
    @Input() showMorePromotionalCardsAsSlide: boolean = true;
    @Input() nrVisibleSlides: number;
    @Input() autoScrollEnabled: boolean = true;

    @Output() onOrderDeal: EventEmitter<EntitlementData> = new EventEmitter<EntitlementData>();
    @Output() onPromoActioned: EventEmitter<PromoEvent> = new EventEmitter<PromoEvent>();

    @ViewChild(TouchableDirective) public touchable: TouchableDirective;
    @ViewChild("carousel", {read: ElementRef}) public carouselER: ElementRef;

    SessionEvents = SessionEvents;
    SlideType = SlideType;
    ScrollType = ScrollType;
    Direction = Direction;
    IconButtonType = IconButtonType;
    AppDealActionStyle = AppDealActionStyle;

    public visibleSlides: PromoSlide[] = [];
    public scrolledBack: boolean = false;
    public scrolledForth: boolean = false;
    public scrolling: ScrollType;
    public ignoreSwipe: boolean = false;

    private baseSlides: PromoSlide[] = [];
    private autoScrollTimer: any;
    private autoScrollBlocked: boolean = false;
    private subscriptions: Subscription = new Subscription();

    constructor(public sessionModel: SessionModel,
                public identityModel: IdentityModel,
                private analyticsService: AnalyticsService,
                private navigationService: NavigationService,
                private element: ElementRef,
                private changeDetectorRef: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.buildSlides();
        this.touchable.touched$.subscribe((touched: boolean) => {
            if (touched) {
                this.autoScrollBlocked = false;
                this.terminateAutoScroll();
            } else if (!this.autoScrollBlocked) {
                this.initiateAutoScroll();
            }
        });
    }

    ngOnChanges(): void {
        this.buildSlides();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    get scrollable(): boolean {
        return this.nrVisibleSlides < this.baseSlides.length;
    }

    get swipeMaxDistance(): number {
        return this.element.nativeElement.clientWidth / this.nrVisibleSlides;
    }

    get swipeRangeProportionOfSize(): number {
        return 1 / this.nrVisibleSlides;
    }

    private buildSlides(): void {
        this.terminateAutoScroll();
        this.scrolledBack = false;
        this.scrolledForth = false;
        this.scrolling = undefined;

        // Build the base list of slides
        this.baseSlides = [];
        if (this.promoteMemberLogin && this.sessionModel.currentApp && this.identityModel.promoteMemberLogin) {
            this.baseSlides.push(new PromoSlide(SlideType.Login));
        }
        this.deals.forEach((deal: EntitlementData) => this.baseSlides.push(new PromoSlide(SlideType.Deal, deal)));
        this.promos.forEach((promo: AppPromoData) => this.baseSlides.push(new PromoSlide(SlideType.Promo, promo)));
        if (this.morePromotionalCards && this.showMorePromotionalCardsAsSlide) {
            this.baseSlides.push(new PromoSlide(SlideType.More));
        }
        this.buildVisibleSlides();
        this.initiateAutoScroll();
        this.changeDetectorRef.detectChanges();
    }

    private buildVisibleSlides(): void {
        this.visibleSlides = this.baseSlides.slice(0, this.nrVisibleSlides);

        // If scrollable, add two "dummy" slides to each end.
        // One slide on each end will be partially visible, and the next slide on each end will become partially visible after scrolling in that direction.
        if (this.scrollable) {
            this.visibleSlides.unshift(this.baseSlides[this.baseSlides.length - 2], this.baseSlides[this.baseSlides.length - 1]);
            this.visibleSlides.push(this.baseSlides[this.nrVisibleSlides % this.baseSlides.length], this.baseSlides[(this.nrVisibleSlides + 1) % this.baseSlides.length]);
        }
    }

    private initiateAutoScroll(): void {
        this.terminateAutoScroll();
        if (this.autoScrollEnabled && this.scrollable) {
            this.autoScrollTimer = setTimeout(() => this.scroll(true, ScrollType.Auto), PromoCarouselComponent.AUTO_SCROLL_MILLIS);
        }
    }

    private terminateAutoScroll(): void {
        if (this.autoScrollTimer) {
            clearTimeout(this.autoScrollTimer);
            this.autoScrollTimer = undefined;
        }
    }

    public blockAutoScroll(): void {
        this.autoScrollBlocked = true;
        this.terminateAutoScroll();
    }

    public scroll(forth: boolean, scrollType: ScrollType, event?: MouseEvent): void {
        if (scrollType === ScrollType.Click) {
            this.ignoreSwipe = true;
            event && event.stopPropagation();
        }

        if (forth && scrollType === ScrollType.Swipe && this.scrolling === ScrollType.Auto) {
            this.scrolling = ScrollType.Swipe;
            this.changeDetectorRef.detectChanges();
            return;
        }

        this.initiateAutoScroll();

        // Shuffle a slide from one end to the other.
        if (forth) {
            this.baseSlides.push(this.baseSlides.shift());
        } else {
            this.baseSlides.unshift(this.baseSlides.pop());
        }
        this.buildVisibleSlides();

        // Immediately compensate for the shuffled slide by moving the slider window without a transition to the current first slide
        this.scrolledBack = !forth;
        this.scrolledForth = forth;
        this.scrolling = undefined;
        this.changeDetectorRef.detectChanges();

        // Once the above has rendered, turn on the transition and move the window back to the new first slide.
        setTimeout(() => {
            this.scrolling = scrollType;
            this.scrolledBack = false;
            this.scrolledForth = false;
            this.changeDetectorRef.detectChanges();
            setTimeout(() => {
                this.ignoreSwipe = false;
                this.scrolling = undefined;
            }, scrollType === ScrollType.Auto ? 1500 : scrollType === ScrollType.Swipe ? 250 : 500);
        });
    }

    public promoActioned(promo: AppPromoData, menuItem: MenuItemData): void {
        this.onPromoActioned.emit(new PromoEvent(promo, menuItem));
    }

    async login(): Promise<void> {
        return this.navigationService.createPage("woa-identification-page", {"mode": IdentificationMode.LOGIN});
    }

    public async showAllDeals(): Promise<void> {
        // noinspection ES6MissingAwait
        this.analyticsService.trackNavOption("deals");
        return this.navigationService.createPage("woa-deals-page");
    }

    public trackSlide(index: number, slide: PromoSlide): string {
        return slide.slideId;
    }
}



// WEBPACK FOOTER //
// ./src/app/promo/promo-carousel/promo-carousel.component.ts